//Libs
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  CalendarDate,
  CalendarDateTime,
  ZonedDateTime,
  today,
  getLocalTimeZone,
  toCalendarDate,
} from "@internationalized/date";
import type { RangeValue } from "@react-types/shared";

//Local
import { DbRead } from "../../database";
import { ExistingPurchaseOrder, POStatus } from "../../models/purchase-order";
import { logger } from "../../logging";
import { useSiteKeyDocStore } from "../../store/site-key-doc";
import PurchaseOrderListPage from "./PurchaseOrderListPage";
import { PURCHASE_ORDERS_URL } from "../../urls";
import DateRangePicker from "../../components/DateRangePicker";

export default function PurchaseOrderListContainer({
  siteKey,
}: {
  siteKey: string;
}) {
  const navigate = useNavigate();

  const [siteKeyDoc] = useSiteKeyDocStore((state) => [state.siteKeyDoc]);

  const [purchaseOrderList, setPurchaseOrderList] = useState<
    ExistingPurchaseOrder[]
  >([]);
  const [range, setRange] = useState<RangeValue<CalendarDate>>({
    start: today(getLocalTimeZone()).subtract({
      days: siteKeyDoc?.customizations?.defaultInvoicePageDateRangeDays ?? 90,
    }),
    end: today(getLocalTimeZone()),
  });

  // Function to navigate to the purchase order detail page
  const goToViewPurchaseOrder = (purchaseOrder: ExistingPurchaseOrder) => {
    navigate(`${PURCHASE_ORDERS_URL}/${purchaseOrder.id}`);
  };

  function handleDateRangeChange(
    value: RangeValue<CalendarDate | ZonedDateTime | CalendarDateTime>,
  ) {
    logger.debug(
      `Invoked handleDateRangeChange for range: ${value.start.month}-${value.start.day} to ${value.end.month}-${value.end.day}`,
    );
    try {
      const convertedStart = toCalendarDate(value.start);
      const convertedEnd = toCalendarDate(value.end);
      const converted = { start: convertedStart, end: convertedEnd };
      setRange(converted);
      logger.debug(
        `set range to ${converted.start.month}-${converted.start.day} to ${converted.end.month}-${converted.end.day}`,
      );
    } catch (e) {
      if (e instanceof Error) {
        console.log("Error in handleDateRangeChange", e);
        logger.error("Error in handleDateRangeChange", e);
      }
    }
  }

  useEffect(() => {
    function getPurchaseOrders() {
      if (!siteKeyDoc) return undefined;
      const startDate = range.start.toDate(siteKeyDoc.timezone);
      const endDate = new Date(
        range.end.toDate(siteKeyDoc.timezone).setHours(23, 59, 59),
      );
      let unsubscribe = undefined;

      unsubscribe = DbRead.purchaseOrders.subscribeAll(
        siteKey,
        startDate,
        endDate,
        null,
        setPurchaseOrderList,
      );
      return unsubscribe;
    }

    const unsubscribeFn = getPurchaseOrders();
    return () => unsubscribeFn && unsubscribeFn();
  }, [siteKey, range, siteKeyDoc]);

  const dateRangePicker = (
    <div className="flex w-full flex-wrap items-center justify-end">
      <span className="pr-2 text-right">Issue Date</span>
      <DateRangePicker
        key={`${range.start.toString()}-${range.end.toString()}`}
        value={range}
        onChange={handleDateRangeChange}
      />
    </div>
  );

  return (
    <PurchaseOrderListPage
      purchaseOrderList={purchaseOrderList}
      siteKey={siteKey}
      goToViewPurchaseOrder={goToViewPurchaseOrder}
    >
      {{
        DateRangePicker: dateRangePicker,
      }}
    </PurchaseOrderListPage>
  );
}
